import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Popconfirm, Popover, Space, Tag } from 'antd';
import React, { useRef, useState } from 'react';
import { queryKeys } from '../../services/react-query/queryKeys';
import {
  MdDelete,
  MdModeEditOutline,
  MdOutlineReceiptLong,
  MdSubtitles,
} from 'react-icons/md';
import Heading from '../../components/common/Heading/Heading';
import DynamicTableAnt from '../../hooks/DynamicTableAnt';
import {
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import StatusTag from '../../hooks/StatusHook';
import {
  deletePurchaseOrder,
  getPurchaseOrder,
} from '../../network/purchaseOrder';
import { useNavigate } from 'react-router-dom';
import { FaFileInvoiceDollar } from 'react-icons/fa6';
import {
  cancelMaterialRequest,
  submitMaterialRequest,
} from '../../network/materialRequest';
import { TiCancel } from 'react-icons/ti';
import PropsSearchTable from '../../components/common/PropsSearchTable/PropsSearchTable';
import { TbDotsVertical } from 'react-icons/tb';
import { PiDotsThreeVerticalBold } from 'react-icons/pi';
import DashboardCards from '../Dashboard/DashboardCards';

const PurchaseOrder = ({ t }) => {
  const navigate = useNavigate();
  const client = useQueryClient();
  // const [searchText, setSearchText] = useState('');
  const searchInput = useRef(null);
  const searchInputMobile = useRef(null);
  const [textValue, setTextValue] = useState('');
  const [searchText, setSearchText] = useState({
    value: '',
    key: '',
  });
  const handleSearch = (selectedKeys, confirm, key) => {
    confirm();
    // console.log(`Searching for: ${selectedKeys[0]}, key: ${key}`);
    setSearchText({ value: selectedKeys[0], key });
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setSearchText({ value: '', key: '' });
  };
  const props = ({ key, name }) =>
    PropsSearchTable({
      key: key,
      name: name,
      setTextValue: setTextValue,
      textValue: textValue,
      setSearchText: setSearchText,
      searchInputMobile: searchInputMobile,
      searchInput: searchInput,
      handleSearch: handleSearch,
      handleReset: handleReset,
      t: t,
    });
  const { message } = App.useApp();
  const handleRemove = useMutation({
    mutationFn: (id) => deletePurchaseOrder(id),
    onSuccess: (data) => {
      console.log(data, 'data');
      if (data?.message?.status === 500) {
        message.error(data?.message?.message);
        return false;
      }
      message.success(data?.message ? data?.message : 'Deleted successfully');
      client.invalidateQueries({ queryKey: [queryKeys.getPurchaseOrder] });
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
  const handleCancle = useMutation({
    mutationFn: (id) => cancelMaterialRequest('Purchase Order', id),
    onSuccess: (data) => {
      console.log(data, 'data');
      if (data?.message?.status === 500) {
        message.error(data?.message?.message);
        return false;
      }
      message.success(data?.message?.message || 'Cancled successfully');
      client.invalidateQueries({ queryKey: [queryKeys.getPurchaseOrder] });
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
  const handleSubmit = useMutation({
    mutationFn: (id) => submitMaterialRequest('Purchase Order', id),
    onSuccess: (data) => {
      console.log(data, 'data');
      if (data?.message?.status === 500) {
        message.error(data?.message?.message);
        return false;
      }
      message.success(data?.message?.message || 'Submitted successfully');
      client.invalidateQueries({ queryKey: [queryKeys.getPurchaseOrder] });
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.getPurchaseOrder, searchText.key, searchText.value],
    queryFn: () =>
      getPurchaseOrder({
        key: searchText.key,
        value: searchText.value,
      }),
    keepPreviousData: true,
  });
  const items = (record) => [
    {
      key: '1',
      label: t.edit,
      icon: <MdModeEditOutline />,
      onClick: () => navigate(`/purchase-order/edit/${record?.name}`),
    },
    {
      type: 'divider',
    },
    {
      key: '3',
      label: t.AddInvoice,
      icon: <FaFileInvoiceDollar />,
      disabled:
        record?.status === 'To Bill'
          ? false
          : record?.status === 'To Receive and Bill'
          ? false
          : true,
      onClick: () => {
        navigate(`/purchase-order/add-invoice/${record?.name}`);
      },
    },
    {
      key: '4',
      label: t.AddReceipt,
      disabled: record?.status === 'To Receive' ? false : true,
      icon: <MdOutlineReceiptLong />,
      onClick: () => navigate(`/purchase-order/add-receipt/${record?.name}`),
    },
    {
      type: 'divider',
    },
    {
      key: '6',
      label: t.submit,
      icon: <CheckOutlined />,
      disabled: record?.status === 'Draft' ? false : true,
      onClick: () => handleSubmit.mutate(record?.name),
    },
    {
      key: '7',
      label: t.Cancel,
      icon: <CloseCircleOutlined />,
      disabled: record?.docstatus === 1 ? false : true,
      onClick: () => handleCancle.mutate(record?.name),
    },
    {
      type: 'divider',
    },
    {
      key: '8',
      label: (
        <Popconfirm
          title={t.Delete + ` (${record?.name})`}
          okText={t.yes}
          cancelText={t.no}
          onConfirm={() => handleRemove.mutate(record?.name)}
        >
          <span style={{ color: 'red' }}>{t.Delete}</span>
        </Popconfirm>
      ),
      icon: <DeleteOutlined style={{ color: 'red' }} />,
    },
  ];
  const columns = [
    {
      title: t.Name,
      dataIndex: 'name',
      key: 'name',
      ...props({ key: 'name', name: t.Name }),
      render: (text, record) => {
        return (
          <span
            style={{ cursor: 'pointer' }}
            className="text-primary"
            onClick={() => {
              navigate(`/purchase-order/edit/${record.name}`);
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: t.supplierName,
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      ...props({ key: 'supplier_name', name: t.supplierName }),
    },
    {
      title: t.Total,
      dataIndex: 'grand_total',
      key: 'grand_total',
    },
    {
      title: t.scheduleDate,
      key: 'schedule_date',
      dataIndex: 'schedule_date',
      ...props({ key: 'schedule_date', name: t.scheduleDate }),
    },
    {
      title: t.status,
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => <StatusTag status={record?.status} />,
      ...props({ key: 'status', name: t.status }),
    },
    {
      title: t.action,
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (text, record) => {
        return (
          <Space>
            <Dropdown
              menu={{ items: items(record) }}
              trigger={['click']}
              overlayClassName="custom-dropdown"
              // placement="bottomCenter"
            >
              <Button
                size="small"
                type="text"
                icon={<PiDotsThreeVerticalBold size={20} color="#86888C" />}
              />
            </Dropdown>
            {/* <Button
              size="small"
              type="primary"
              onClick={() => {
                navigate(`/purchase-order/edit/${record?.name}`);
              }}
              icon={<MdModeEditOutline />}
            />
            {record?.status === 'Draft' && (
              <Popover
                placement="top"
                title={t.submit}
                onClick={() => {
                  handleSubmit.mutate(record?.name);
                }}
              >
                <Button
                  size="small"
                  type="primary"
                  style={{ backgroundColor: '#28a745' }}
                  icon={<MdSubtitles />}
                />
              </Popover>
            )}
            {record?.docstatus === 1 && (
              <Popover
                placement="top"
                title={t.Cancel}
                onClick={() => {
                  handleCancle.mutate(record?.name);
                }}
              >
                <Button
                  size="small"
                  type="primary"
                  style={{ backgroundColor: '#ff4d4f' }}
                  icon={<TiCancel />}
                />
              </Popover>
            )}
            {record?.status === 'To Bill' && (
              <Popover
                placement="top"
                title={t.AddInvoice}
                onClick={() => {
                  navigate(`/purchase-order/add-invoice/${record?.name}`);
                }}
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<FaFileInvoiceDollar />}
                  style={{
                    backgroundColor: '#2a9790',
                  }}
                />
              </Popover>
            )}
            {record?.status === 'To Receive and Bill' && (
              <>
                <Popover
                  placement="top"
                  title={t.AddInvoice}
                  onClick={() => {
                    navigate(`/purchase-order/add-invoice/${record?.name}`);
                  }}
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<FaFileInvoiceDollar />}
                    style={{
                      backgroundColor: '#2a9790',
                    }}
                  />
                </Popover>
                <Popover
                  placement="top"
                  title={t.AddReceipt}
                  onClick={() => {
                    navigate(`/purchase-order/add-receipt/${record?.name}`);
                  }}
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<MdOutlineReceiptLong />}
                    style={{
                      backgroundColor: '#008bd0',
                    }}
                  />
                </Popover>
              </>
            )}
            {record?.status === 'To Receive' && (
              <Popover
                placement="top"
                title={t.AddReceipt}
                onClick={() => {
                  navigate(`/purchase-order/add-receipt/${record?.name}`);
                }}
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<MdOutlineReceiptLong />}
                  style={{
                    backgroundColor: '#008bd0',
                  }}
                />
              </Popover>
            )}
            {record?.docstatus !== 1 && (
              <Popconfirm
                title={t.DeletetheWarehouse + ` (${record?.name})`}
                description={t.areYouSure}
                onConfirm={() => handleRemove.mutate(record?.name)}
                okText={t.yes}
                cancelText={t.no}
              >
                <Button
                  size="small"
                  type="primary"
                  danger
                  icon={<MdDelete />}
                />
              </Popconfirm>
            )} */}
          </Space>
        );
      },
    },
  ];
  const dataTable = data?.data?.map((item, index) => {
    return {
      key: index,
      ...item,
    };
  });
  return (
    <div className="inventory">
      {/* <Heading title={t.Inventory} subTitle={t.purchaseOrder} path="/" /> */}
      <DashboardCards t={t} />
      <div className="mt-5">
        <DynamicTableAnt
          columns={columns}
          data={dataTable}
          loading={isLoading}
          setSearchText={setSearchText}
          Placeholder={t.searchByName}
          buttonModal={
            <Button
              type="primary"
              onClick={() => {
                navigate('/purchase-order/add');
              }}
            >
              <PlusOutlined
                style={{
                  fontSize: '10px',
                }}
              />
              {t.addPurchaseOrder}
            </Button>
          }
        />
      </div>
    </div>
  );
};

export default PurchaseOrder;
